/**
 * Planning des passages + tournées — URL canonique `/admin/planificateur` (les anciennes URLs `/planificateur` redirigent).
 * Anciennes routes « descente » restent disponibles ailleurs ; cette page est centrée Passage → Tournée.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  ChevronRight,
  ClipboardList,
  Factory,
  FileSpreadsheet,
  Layers,
  Play,
  RefreshCw,
  Route,
  Square,
  Truck,
  X,
} from "lucide-react";
import { api } from "../../../lib/axios";
import { notifyAdminExecutionPassagesRefresh } from "../../../modules/execution/services/execution.service";
import { ClientDemandesAdminPanel } from "../../../components/planificateur/ClientDemandesAdminPanel";
import type { EquipeOption, PassageApiItem, TourneeApiItem, ZoneOption } from "./planningTypes";
import {
  STATUTS_PASSAGE_FILTRE,
  STATUTS_TOURNEE_FILTRE,
  clientLabel,
  computeKpis,
  isLatePassage,
  localIsoDate,
  passageKind,
  passageKindLabel,
  statutBadgeClass,
} from "./planningDashboardUtils";

type FiltersState = {
  date: string;
  zoneId: string;
  clientId: string;
  statutPassage: string;
  tourneeId: string;
  statutTournee: string;
  equipeId: string;
  urgenceOnly: boolean;
  unassignedOnly: boolean;
};

function KpiTile(props: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: "emerald" | "amber" | "rose" | "sky" | "slate";
}): React.JSX.Element {
  const ring =
    props.accent === "amber"
      ? "ring-amber-500/20"
      : props.accent === "rose"
        ? "ring-rose-500/20"
        : props.accent === "sky"
          ? "ring-sky-500/20"
          : props.accent === "slate"
            ? "ring-slate-500/25"
            : "ring-emerald-500/20";
  return (
    <article className={`rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ${ring}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{props.label}</p>
      <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-white">{props.value}</p>
      {props.hint ? <p className="mt-1 text-xs text-slate-500">{props.hint}</p> : null}
    </article>
  );
}

export default function PlanificateurDashboard(): React.JSX.Element {
  const metierBase = "/admin";
  const planBase = "/admin/planificateur";
  const todayIso = useMemo(() => localIsoDate(new Date()), []);

  const [filters, setFilters] = useState<FiltersState>({
    date: "",
    zoneId: "",
    clientId: "",
    statutPassage: "",
    tourneeId: "",
    statutTournee: "",
    equipeId: "",
    urgenceOnly: false,
    unassignedOnly: false,
  });

  const [passages, setPassages] = useState<PassageApiItem[]>([]);
  const [passagesToday, setPassagesToday] = useState<PassageApiItem[]>([]);
  const [tournees, setTournees] = useState<TourneeApiItem[]>([]);
  const [zones, setZones] = useState<ZoneOption[]>([]);
  const [equipes, setEquipes] = useState<EquipeOption[]>([]);
  const [loading, setLoading] = useState(false);
  /** Zone du client lorsque le filtre « Client ID » est renseigné (pour filtrer les équipes). */
  const [clientZoneIdFromFilter, setClientZoneIdFromFilter] = useState<number | null>(null);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [drawerPassage, setDrawerPassage] = useState<PassageApiItem | null>(null);

  const [assignTourneeId, setAssignTourneeId] = useState("");
  const [assignPassageId, setAssignPassageId] = useState<number | null>(null);

  const [batchOpen, setBatchOpen] = useState(false);
  const [batchDate, setBatchDate] = useState(todayIso);
  const [batchZone, setBatchZone] = useState("");
  const [batchEquipe, setBatchEquipe] = useState("");

  const [genContratId, setGenContratId] = useState("");
  const [genDebut, setGenDebut] = useState(todayIso);
  const [genFin, setGenFin] = useState(todayIso);

  const tourneeById = useMemo(() => {
    const m = new Map<number, TourneeApiItem>();
    for (const t of tournees) m.set(t.id, t);
    return m;
  }, [tournees]);

  const buildPassageParams = useCallback((): Record<string, string> => {
    const p: Record<string, string> = {
      limit: "100",
      page: "1",
      date: filters.date.trim() || todayIso,
    };
    if (filters.zoneId.trim()) p.zoneId = filters.zoneId.trim();
    if (filters.clientId.trim()) p.clientId = filters.clientId.trim();
    if (filters.statutPassage.trim()) p.statut = filters.statutPassage.trim();
    if (filters.tourneeId.trim()) p.tourneeId = filters.tourneeId.trim();
    if (filters.urgenceOnly) p.isSuspect = "true";
    return p;
  }, [filters, todayIso]);

  const loadReference = useCallback(async () => {
    try {
      const [zRes, eRes] = await Promise.all([api.get("/zones"), api.get("/equipes")]);
      const zRaw = Array.isArray(zRes.data) ? zRes.data : zRes.data?.data ?? [];
      const eRaw = Array.isArray(eRes.data) ? eRes.data : eRes.data?.data ?? [];
      setZones(zRaw as ZoneOption[]);
      setEquipes(eRaw as EquipeOption[]);
    } catch {
      /* zones / équipes optionnels pour filtres */
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const paramsMain = buildPassageParams();
      const paramsToday: Record<string, string> = {
        date: todayIso,
        limit: "100",
        page: "1",
      };
      const tourParams: Record<string, string> = { limit: "100", page: "1" };
      if (filters.date.trim()) tourParams.date = filters.date.trim();
      else tourParams.date = todayIso;
      if (filters.zoneId.trim()) tourParams.zoneId = filters.zoneId.trim();
      if (filters.statutTournee.trim()) tourParams.statut = filters.statutTournee.trim();

      const [pMain, pToday, tRes] = await Promise.all([
        api.get("/passages", { params: paramsMain }),
        api.get("/passages", { params: paramsToday }),
        api.get("/tournees", { params: tourParams }),
      ]);

      const itemsMain = (pMain.data?.items ?? []) as PassageApiItem[];
      const itemsToday = (pToday.data?.items ?? []) as PassageApiItem[];
      const itemsT = (tRes.data?.items ?? []) as TourneeApiItem[];

      setPassages(itemsMain);
      setPassagesToday(itemsToday);
      setTournees(itemsT);
    } catch {
      toast.error("Impossible de charger passages ou tournées.");
      setPassages([]);
      setPassagesToday([]);
      setTournees([]);
    } finally {
      setLoading(false);
    }
  }, [buildPassageParams, filters.date, filters.statutTournee, filters.zoneId, todayIso]);

  useEffect(() => {
    void loadReference();
  }, [loadReference]);

  useEffect(() => {
    const cid = Number(filters.clientId);
    if (!Number.isInteger(cid) || cid <= 0) {
      setClientZoneIdFromFilter(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/clients/${cid}`)
      .then((res) => {
        if (cancelled) return;
        const z = res.data?.zone?.id ?? res.data?.zoneId ?? null;
        setClientZoneIdFromFilter(typeof z === "number" ? z : null);
      })
      .catch(() => {
        if (!cancelled) setClientZoneIdFromFilter(null);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.clientId]);

  const effectiveZoneIdForEquipes = useMemo(() => {
    const zf = filters.zoneId.trim();
    if (zf) {
      const n = Number(zf);
      return Number.isInteger(n) && n > 0 ? n : null;
    }
    return clientZoneIdFromFilter;
  }, [filters.zoneId, clientZoneIdFromFilter]);

  /** Équipes dont la zone = zone filtre ou zone du client (équipe affectée sur la tournée, pas sur le client). */
  const equipesFiltered = useMemo(() => {
    if (effectiveZoneIdForEquipes == null) return equipes;
    return equipes.filter((e) => {
      const zid = e.zone?.id ?? e.zoneId ?? undefined;
      return zid === effectiveZoneIdForEquipes;
    });
  }, [equipes, effectiveZoneIdForEquipes]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const kpis = useMemo(
    () => computeKpis(passagesToday, passages),
    [passages, passagesToday]
  );

  const passagesFiltered = useMemo(() => {
    let rows = passages;
    if (filters.unassignedOnly) {
      rows = rows.filter((p) => !p.tourneeId);
    }
    if (filters.equipeId.trim()) {
      const eid = Number(filters.equipeId);
      if (Number.isInteger(eid) && eid > 0) {
        rows = rows.filter((p) => {
          const tn = p.tourneeId != null ? tourneeById.get(p.tourneeId) : undefined;
          return tn?.equipe?.id === eid;
        });
      }
    }
    return rows;
  }, [filters.equipeId, filters.unassignedOnly, passages, tourneeById]);

  const unassignedList = useMemo(
    () => passages.filter((p) => !p.tourneeId),
    [passages]
  );

  const toggleSelect = (id: number): void => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const exportExcel = (): void => {
    if (!passagesFiltered.length) {
      toast.warning("Aucune ligne à exporter.");
      return;
    }
    const rows = passagesFiltered.map((p) => ({
      Client: clientLabel(p),
      Passage: `#${p.id}`,
      DatePrevu: p.datePrevue,
      Zone: p.zone?.nom ?? "",
      Type: passageKindLabel(passageKind(p)),
      Statut: p.statut,
      Tournee: p.tournee?.numero ?? (p.tourneeId != null ? `#${p.tourneeId}` : ""),
      Equipe:
        p.tourneeId != null ? tourneeById.get(p.tourneeId)?.equipe?.nom ?? "" : "",
      Facturable: p.isFacturable ? "oui" : "non",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Passages");
    XLSX.writeFile(wb, "planning-passages.xlsx");
    toast.success("Export généré.");
  };

  const handleGeneratePassages = async (): Promise<void> => {
    const cid = Number(genContratId);
    if (!Number.isInteger(cid) || cid <= 0) {
      toast.warning("Indiquez un identifiant de contrat valide.");
      return;
    }
    try {
      const res = await api.post("/passages/generate-for-contrat", {
        contratId: cid,
        periodeDebut: new Date(genDebut).toISOString(),
        periodeFin: new Date(genFin).toISOString(),
      });
      toast.success(
        `Génération : ${res.data?.created?.length ?? 0} créé(s), ${res.data?.skipped ?? 0} ignoré(s).`
      );
      await refresh();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error ?? "Erreur génération.");
    }
  };

  const openAssign = (passageId: number): void => {
    setAssignPassageId(passageId);
    setAssignTourneeId("");
  };

  const submitAssign = async (): Promise<void> => {
    if (assignPassageId == null) return;
    const tid = Number(assignTourneeId);
    if (!Number.isInteger(tid) || tid <= 0) {
      toast.warning("Identifiant de tournée invalide.");
      return;
    }
    try {
      await api.post(`/passages/${assignPassageId}/assign-tournee`, { tourneeId: tid });
      toast.success("Passage affecté.");
      setAssignPassageId(null);
      await refresh();
    } catch {
      toast.error("Affectation impossible.");
    }
  };

  const submitBatchTournee = async (): Promise<void> => {
    if (selectedIds.length === 0) {
      toast.warning("Sélectionnez au moins un passage.");
      return;
    }
    try {
      const body: Record<string, unknown> = { date: new Date(batchDate).toISOString() };
      if (batchZone.trim()) body.zoneId = Number(batchZone);
      if (batchEquipe.trim()) body.equipeId = Number(batchEquipe);
      const res = await api.post("/tournees", body);
      const tid = res.data?.tournee?.id as number | undefined;
      if (!tid) {
        toast.error("Réponse tournée inattendue.");
        return;
      }
      await api.post(`/tournees/${tid}/assign-passages`, { passageIds: selectedIds });
      toast.success(`Tournée #${tid} créée et passages affectés.`);
      setBatchOpen(false);
      setSelectedIds([]);
      await refresh();
    } catch {
      toast.error("Création / affectation impossible.");
    }
  };

  const patchStatut = async (id: number, statut: string): Promise<void> => {
    try {
      await api.patch(`/passages/${id}/status`, { statut });
      toast.success("Statut mis à jour.");
      await refresh();
    } catch {
      toast.error("Changement de statut impossible.");
    }
  };

  const startPassage = async (id: number): Promise<void> => {
    try {
      await api.post(`/passages/${id}/start`, {});
      toast.success("Exécution démarrée.");
      await refresh();
    } catch {
      toast.error("Démarrage impossible (statut ou validation GPS).");
    }
  };

  const startTournee = async (id: number): Promise<void> => {
    try {
      await api.post(`/tournees/${id}/start`);
      notifyAdminExecutionPassagesRefresh();
      toast.success("Tournée démarrée.");
      await refresh();
    } catch {
      toast.error("Démarrage tournée impossible.");
    }
  };

  const finishTournee = async (id: number): Promise<void> => {
    try {
      await api.post(`/tournees/${id}/finish`);
      toast.success("Tournée terminée.");
      await refresh();
    } catch {
      toast.error("Clôture impossible.");
    }
  };

  const emptyPassages = !loading && passagesFiltered.length === 0;

  return (
    <div className="space-y-6 p-4 text-slate-100 md:p-6">
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/35 p-5 shadow-xl md:p-6">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/90">Planification</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight text-white md:text-3xl">
              Planning des passages
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400">
              Passages générés depuis les abonnements, affectation aux tournées et suivi opérationnel.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <Link
              to={`${planBase}/passages`}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Route className="h-4 w-4" />
              Passages (liste dédiée)
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <KpiTile label="Passages aujourd’hui" value={kpis.passagesAujourdhui} hint="API jour civil" accent="emerald" />
        <KpiTile label="Non affectés" value={kpis.nonAffectes} accent="amber" />
        <KpiTile label="En retard" value={kpis.enRetard} accent="rose" />
        <KpiTile label="Urgences client" value={kpis.urgencesClient} hint="isSuspect" accent="rose" />
        <KpiTile label="En cours" value={kpis.enCours} accent="sky" />
        <KpiTile
          label="Validés / facturables"
          value={kpis.validesFacturables}
          hint="Effectué ou attente validation + facturable"
          accent="emerald"
        />
      </section>

      <section className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => document.getElementById("block-generer-passages")?.scrollIntoView({ behavior: "smooth" })}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
        >
          <Layers className="h-4 w-4" />
          Générer passages
        </button>
        <button
          type="button"
          onClick={() => document.getElementById("zone-non-affectes")?.scrollIntoView({ behavior: "smooth" })}
          className="inline-flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-500/15"
        >
          <AlertTriangle className="h-4 w-4" />
          Passages non affectés
        </button>
        <Link
          to={`${planBase}/tournees`}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Truck className="h-4 w-4" />
          Créer tournée
        </Link>
        <Link
          to={`${planBase}/tournees`}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <ClipboardList className="h-4 w-4" />
          Voir tournées
        </Link>
        <button
          type="button"
          onClick={() => exportExcel()}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <FileSpreadsheet className="h-4 w-4" />
          Export
        </button>
      </section>

      <section
        id="block-generer-passages"
        className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5"
      >
        <h2 className="text-lg font-semibold text-white">Générer des passages (contrat)</h2>
        <p className="mt-1 text-xs text-slate-500">
          POST /api/passages/generate-for-contrat — période et contrat requis.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Contrat ID
            <input
              type="number"
              value={genContratId}
              onChange={(e) => setGenContratId(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="ex. 12"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Début
            <input
              type="date"
              value={genDebut}
              onChange={(e) => setGenDebut(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Fin
            <input
              type="date"
              value={genFin}
              onChange={(e) => setGenFin(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleGeneratePassages()}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
          >
            Lancer la génération
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Filtres</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Date (jour prévu)
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Zone
            <select
              value={filters.zoneId}
              onChange={(e) => setFilters((f) => ({ ...f, zoneId: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Toutes</option>
              {zones.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.nom ?? `Zone #${z.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Client ID
            <input
              type="number"
              value={filters.clientId}
              onChange={(e) => setFilters((f) => ({ ...f, clientId: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="—"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Statut passage
            <select
              value={filters.statutPassage}
              onChange={(e) => setFilters((f) => ({ ...f, statutPassage: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              {STATUTS_PASSAGE_FILTRE.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "Tous"}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Tournée ID
            <input
              type="number"
              value={filters.tourneeId}
              onChange={(e) => setFilters((f) => ({ ...f, tourneeId: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="—"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Équipe (via tournée)
            <select
              value={filters.equipeId}
              onChange={(e) => setFilters((f) => ({ ...f, equipeId: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Toutes</option>
              {equipesFiltered.map((eq) => (
                <option key={eq.id} value={String(eq.id)}>
                  {eq.nom ?? `Équipe #${eq.id}`}
                  {eq.capaciteJour != null ? ` · cap. ${eq.capaciteJour}` : ""}
                </option>
              ))}
            </select>
            {effectiveZoneIdForEquipes != null ? (
              <span className="text-[10px] text-slate-500">
                Filtré par zone #{effectiveZoneIdForEquipes} (filtre zone ou zone du client).
              </span>
            ) : (
              <span className="text-[10px] text-slate-500">
                Sélectionnez une zone ou un client pour afficher les équipes de ce secteur.
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Statut tournée (liste du jour)
            <select
              value={filters.statutTournee}
              onChange={(e) => setFilters((f) => ({ ...f, statutTournee: e.target.value }))}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              {STATUTS_TOURNEE_FILTRE.map((s) => (
                <option key={s || "all-t"} value={s}>
                  {s || "Tous"}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-col justify-end gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={filters.urgenceOnly}
                onChange={(e) => setFilters((f) => ({ ...f, urgenceOnly: e.target.checked }))}
                className="rounded border-slate-600"
              />
              Urgence uniquement (isSuspect)
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={filters.unassignedOnly}
                onChange={(e) => setFilters((f) => ({ ...f, unassignedOnly: e.target.checked }))}
                className="rounded border-slate-600"
              />
              Non affectés uniquement
            </label>
          </div>
        </div>
      </section>

      {emptyPassages ? (
        <section className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-10 text-center">
          <Factory className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-4 text-lg font-semibold text-white">Aucun passage généré pour cette période.</p>
          <p className="mt-2 text-sm text-slate-400">
            Les passages doivent être générés depuis les abonnements actifs avant d’être affectés aux tournées.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => document.getElementById("block-generer-passages")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
            >
              Générer passages
            </button>
            <Link
              to={`${metierBase}/contrats`}
              className="rounded-xl border border-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Voir abonnements
            </Link>
            <Link
              to={`${metierBase}/categories-tarifs`}
              className="rounded-xl border border-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Voir plans d’abonnement
            </Link>
          </div>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-white">Passages</h2>
          <span className="text-xs text-slate-500">{passagesFiltered.length} ligne(s)</span>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-[1100px] w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Passage</th>
                <th className="px-3 py-3">Date / heure</th>
                <th className="px-3 py-3">Zone</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Statut</th>
                <th className="px-3 py-3">Tournée</th>
                <th className="px-3 py-3">Équipe</th>
                <th className="px-3 py-3">Validation</th>
                <th className="px-3 py-3">Facturable</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/90">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-3 py-10 text-center text-slate-500">
                    Chargement…
                  </td>
                </tr>
              ) : passagesFiltered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-10 text-center text-slate-500">
                    Aucun résultat avec ces filtres.
                  </td>
                </tr>
              ) : (
                passagesFiltered.map((p) => {
                  const tn = p.tourneeId != null ? tourneeById.get(p.tourneeId) : undefined;
                  const kind = passageKind(p);
                  const late = isLatePassage(p);
                  const validationLabel = p.validatedAt
                    ? "Validé"
                    : p.statut.toUpperCase() === "EN_ATTENTE_VALIDATION"
                      ? "Attente"
                      : "—";
                  const tourReady =
                    p.tournee?.statut === "PLANIFIEE" ||
                    p.tournee?.statut === "EN_COURS" ||
                    tn?.statut === "PLANIFIEE" ||
                    tn?.statut === "EN_COURS";

                  return (
                    <tr key={p.id} className="bg-slate-900/30">
                      <td className="px-3 py-2 text-white">{clientLabel(p)}</td>
                      <td className="px-3 py-2 font-mono text-emerald-300">#{p.id}</td>
                      <td className="px-3 py-2 text-slate-300">
                        {new Date(p.datePrevue).toLocaleString("fr-FR")}
                        {late ? (
                          <span className="ml-2 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300">
                            Retard
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-slate-400">{p.zone?.nom ?? "—"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-lg px-2 py-0.5 text-xs font-semibold ring-1 ${
                            kind === "urgence"
                              ? "bg-rose-500/15 text-rose-200 ring-rose-400/30"
                              : "bg-slate-800 text-slate-200 ring-slate-600/40"
                          }`}
                        >
                          {passageKindLabel(kind)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded-lg px-2 py-0.5 text-xs font-semibold ring-1 ${statutBadgeClass(p.statut)}`}>
                          {p.statut}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-300">
                        {p.tournee?.numero ?? (p.tourneeId != null ? `#${p.tourneeId}` : "—")}
                      </td>
                      <td className="px-3 py-2 text-slate-300">{tn?.equipe?.nom ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-400">{validationLabel}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-lg px-2 py-0.5 text-xs font-bold ${
                            p.isFacturable
                              ? "bg-emerald-500/15 text-emerald-300"
                              : "bg-slate-700/50 text-slate-400"
                          }`}
                        >
                          {p.isFacturable ? "Oui" : "Non"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openAssign(p.id)}
                            className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-800"
                          >
                            Affecter
                          </button>
                          <button
                            type="button"
                            onClick={() => setDrawerPassage(p)}
                            className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                          >
                            Détail
                          </button>
                          <button
                            type="button"
                            disabled={!p.tourneeId || !tourReady}
                            title={!p.tourneeId ? "Affectez d’abord une tournée" : undefined}
                            onClick={() => void startPassage(p.id)}
                            className="rounded-lg border border-emerald-600/40 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-950/40 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Démarrer
                          </button>
                          <button
                            type="button"
                            onClick={() => void patchStatut(p.id, "REPORTE")}
                            className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                          >
                            Reporter
                          </button>
                          <button
                            type="button"
                            onClick={() => void patchStatut(p.id, "ANNULE")}
                            className="rounded-lg border border-rose-900/50 px-2 py-1 text-xs text-rose-300 hover:bg-rose-950/30"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            disabled
                            title="Lien portail / demande — données agrégées non exposées sur la liste"
                            className="cursor-not-allowed rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-600"
                          >
                            Demande client
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section id="zone-non-affectes" className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Passages non affectés</h2>
            <p className="text-xs text-amber-200/70">{unassignedList.length} passage(s) sans tournée (extrait chargement courant).</p>
          </div>
          <button
            type="button"
            disabled={selectedIds.length === 0}
            onClick={() => setBatchOpen(true)}
            className="rounded-xl bg-amber-500/90 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Créer tournée avec sélection ({selectedIds.length})
          </button>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2"> </th>
                <th className="px-3 py-2">Passage</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Date prévue</th>
                <th className="px-3 py-2">Zone</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/90">
              {unassignedList.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                    Aucun passage sans tournée dans le jeu chargé.
                  </td>
                </tr>
              ) : (
                unassignedList.map((p) => (
                  <tr key={p.id} className="bg-slate-900/30">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        className="rounded border-slate-600"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-emerald-300">#{p.id}</td>
                    <td className="px-3 py-2 text-white">{clientLabel(p)}</td>
                    <td className="px-3 py-2 text-slate-400">{new Date(p.datePrevue).toLocaleString("fr-FR")}</td>
                    <td className="px-3 py-2 text-slate-400">{p.zone?.nom ?? "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Tournées du jour (filtres date / zone / statut)</h2>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-[960px] w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-3">N°</th>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Zone</th>
                <th className="px-3 py-3">Équipe</th>
                <th className="px-3 py-3">Véhicule</th>
                <th className="px-3 py-3">Chauffeur</th>
                <th className="px-3 py-3">Passages</th>
                <th className="px-3 py-3">Statut</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/90">
              {tournees.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                    {loading ? "Chargement…" : "Aucune tournée pour ces filtres."}
                  </td>
                </tr>
              ) : (
                tournees.map((t) => (
                  <tr key={t.id} className="bg-slate-900/30">
                    <td className="px-3 py-2 font-mono text-emerald-300">{t.numero ?? `#${t.id}`}</td>
                    <td className="px-3 py-2 text-slate-300">{new Date(t.date).toLocaleDateString("fr-FR")}</td>
                    <td className="px-3 py-2 text-slate-400">{t.zone?.nom ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-300">{t.equipe?.nom ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-400">{t.vehicule?.immatriculation ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-400">
                      {t.chauffeur ? `${t.chauffeur.prenom ?? ""} ${t.chauffeur.nom ?? ""}`.trim() : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-300">{t._count?.passages ?? 0}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-lg px-2 py-0.5 text-xs font-semibold ring-1 ${statutBadgeClass(t.statut)}`}>
                        {t.statut}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          title="Voir détail API"
                          onClick={() => toast.info(`Tournée #${t.id} — ouvrir l’écran Tournées pour plus de détail.`)}
                          className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-800"
                        >
                          Voir
                        </button>
                        <button
                          type="button"
                          title="Démarrer"
                          onClick={() => void startTournee(t.id)}
                          className="rounded-lg border border-emerald-600/50 p-1.5 text-emerald-400 hover:bg-emerald-950/50"
                        >
                          <Play className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Terminer"
                          onClick={() => void finishTournee(t.id)}
                          className="rounded-lg border border-slate-600 p-1.5 text-slate-300 hover:bg-slate-800"
                        >
                          <Square className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ClientDemandesAdminPanel crossLinkTo="/admin/interventions" crossLinkLabel="Exécution passages →" />

      {assignPassageId != null ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Affecter le passage #{assignPassageId}</h3>
            <p className="mt-1 text-xs text-slate-500">POST /api/passages/:id/assign-tournee</p>
            <label className="mt-4 flex flex-col gap-1 text-xs text-slate-400">
              ID tournée
              <input
                type="number"
                value={assignTourneeId}
                onChange={(e) => setAssignTourneeId(e.target.value)}
                className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAssignPassageId(null)}
                className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => void submitAssign()}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {batchOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Créer une tournée et affecter {selectedIds.length} passage(s)</h3>
            <p className="mt-1 text-xs text-slate-500">
              POST /api/tournees puis POST /api/tournees/:id/assign-passages
            </p>
            <div className="mt-4 grid gap-3">
              <label className="flex flex-col gap-1 text-xs text-slate-400">
                Date
                <input
                  type="date"
                  value={batchDate}
                  onChange={(e) => setBatchDate(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-400">
                Zone ID (optionnel)
                <input
                  type="number"
                  value={batchZone}
                  onChange={(e) => setBatchZone(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-400">
                Équipe sur la tournée (optionnel)
                <select
                  value={batchEquipe}
                  onChange={(e) => setBatchEquipe(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                >
                  <option value="">— Aucune —</option>
                  {equipesFiltered.map((eq) => (
                    <option key={eq.id} value={String(eq.id)}>
                      {eq.nom ?? `Équipe #${eq.id}`}
                      {eq.capaciteJour != null ? ` · cap. ${eq.capaciteJour}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBatchOpen(false)}
                className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={() => void submitBatchTournee()}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                Créer & affecter
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {drawerPassage ? (
        <div className="fixed inset-0 z-[130] flex justify-end bg-black/60 backdrop-blur-sm">
          <div className="flex h-full w-full max-w-lg flex-col border-l border-slate-800 bg-slate-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">Passage</p>
                <p className="text-xl font-bold text-white">#{drawerPassage.id}</p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerPassage(null)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
              <DetailRow label="Client" value={clientLabel(drawerPassage)} />
              <DetailRow label="Contrat" value={`#${drawerPassage.contratId}`} />
              <DetailRow
                label="Dotations / détail contrat"
                value="Non chargé (pas de GET détail dans ce flux liste)"
              />
              <DetailRow
                label="Date prévue"
                value={new Date(drawerPassage.datePrevue).toLocaleString("fr-FR")}
              />
              <DetailRow
                label="Historique demande client"
                value="Non disponible sur cet endpoint — voir panneau Demandes portail"
              />
              <DetailRow
                label="Tournée"
                value={
                  drawerPassage.tournee?.numero ??
                  (drawerPassage.tourneeId != null ? `#${drawerPassage.tourneeId}` : "—")
                }
              />
              <DetailRow
                label="Équipe"
                value={
                  drawerPassage.tourneeId != null
                    ? tourneeById.get(drawerPassage.tourneeId)?.equipe?.nom ?? "—"
                    : "—"
                }
              />
              <DetailRow label="Statut terrain" value={drawerPassage.statut} />
              <DetailRow
                label="Mission"
                value={drawerPassage.mission?.numero ?? "—"}
              />
              <DetailRow
                label="Intervention"
                value={drawerPassage.intervention?.titre ?? "—"}
              />
              <DetailRow
                label="Preuves"
                value={
                  drawerPassage.preuves?.length
                    ? `${drawerPassage.preuves.length} fichier(s)`
                    : "Aucune dans la vue liste"
                }
              />
              <DetailRow
                label="GPS"
                value={
                  drawerPassage.startLatitude != null && drawerPassage.startLongitude != null
                    ? `${drawerPassage.startLatitude.toFixed(5)}, ${drawerPassage.startLongitude.toFixed(5)}`
                    : "—"
                }
              />
              <DetailRow
                label="Facturable"
                value={drawerPassage.isFacturable ? "Oui" : "Non"}
              />
            </div>
          </div>
        </div>
      ) : null}

      <p className="text-center text-xs text-slate-600">
        Historique des passages :{" "}
        <Link className="text-emerald-400 underline" to="/admin/passages/historique">
          Ouvrir l’historique
        </Link>
      </p>
    </div>
  );
}

function DetailRow(props: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{props.label}</p>
      <p className="mt-1 text-slate-200">{props.value}</p>
    </div>
  );
}
